/**
 * Feature Flags Configuration
 * 
 * This file centralizes all feature flag logic for the application.
 * Feature flags allow us to enable/disable features without code changes.
 * 
 * To enable/disable features, modify the environment variables in .env file:
 * - REACT_APP_FEATURE_GROUPS_ENABLED=true/false
 * - REACT_APP_FEATURE_EVENTS_ENABLED=true/false
 */

export const FEATURES = {
  // Groups feature - includes group creation, management, rides, discussions
  GROUPS_ENABLED: process.env.REACT_APP_FEATURE_GROUPS_ENABLED === 'true',
  
  // Events feature - includes event creation, management, and event rides
  EVENTS_ENABLED: process.env.REACT_APP_FEATURE_EVENTS_ENABLED === 'true',
  
  // Multi-country feature - enables country-based content filtering
  MULTI_COUNTRY_ENABLED: process.env.REACT_APP_FEATURE_MULTI_COUNTRY === 'true',
  
  // Country configuration
  DEFAULT_COUNTRY: process.env.REACT_APP_DEFAULT_COUNTRY || 'LV',
  AVAILABLE_COUNTRIES: process.env.REACT_APP_AVAILABLE_COUNTRIES?.split(',') || ['LV'],
};

/**
 * Helper function to check if a feature is enabled
 * @param {string} featureName - Name of the feature to check
 * @returns {boolean} - Whether the feature is enabled
 */
export const isFeatureEnabled = (featureName) => {
  return FEATURES[featureName] || false;
};

/**
 * Helper function to get country name from code
 * @param {string} countryCode - ISO country code (e.g., 'LV')
 * @returns {string} - Country display name
 */
export const getCountryName = (countryCode) => {
  const countryNames = {
    'LV': 'Latvia',
    'EE': 'Estonia', 
    'LT': 'Lithuania',
    'SE': 'Sweden',
    'NO': 'Norway',
    'DK': 'Denmark',
    'FI': 'Finland',
    'DE': 'Germany',
    'PL': 'Poland',
    'NL': 'Netherlands',
    'BE': 'Belgium',
    'AT': 'Austria',
    'CZ': 'Czech Republic',
    'SK': 'Slovakia'
  };
  return countryNames[countryCode] || countryCode;
};

/**
 * Get available countries with names
 * @returns {Array} Array of {code, name} objects
 */
export const getAvailableCountries = () => {
  return FEATURES.AVAILABLE_COUNTRIES.map(code => ({
    code,
    name: getCountryName(code)
  }));
};

/**
 * Check if a country is available
 * @param {string} countryCode - ISO country code
 * @returns {boolean} - Whether the country is available
 */
export const isCountryAvailable = (countryCode) => {
  return FEATURES.AVAILABLE_COUNTRIES.includes(countryCode);
};

/**
 * Log current feature flag status (useful for debugging)
 */
export const logFeatureFlags = () => {
  if (process.env.NODE_ENV === 'development') {
    console.log('🚩 Feature Flags Status:');
    console.log('  Groups:', FEATURES.GROUPS_ENABLED ? '✅ Enabled' : '❌ Disabled');
    console.log('  Events:', FEATURES.EVENTS_ENABLED ? '✅ Enabled' : '❌ Disabled');
    console.log('  Multi-Country:', FEATURES.MULTI_COUNTRY_ENABLED ? '✅ Enabled' : '❌ Disabled');
    if (FEATURES.MULTI_COUNTRY_ENABLED) {
      console.log('    Default Country:', FEATURES.DEFAULT_COUNTRY);
      console.log('    Available Countries:', FEATURES.AVAILABLE_COUNTRIES.join(', '));
    }
  }
};

// Log feature flags on import (development only)
logFeatureFlags();

export default FEATURES;